import { showInfo } from '../libs/react.toastify';
import { isFaceLandmarkIndex, pointsConnections, rectangleFlag } from '../constants/constants';

// The backend sends `rectangleColor` in every socket payload: either a numeric
// rectangleFlag or a raw CSS color string. Both the framing border and the pose
// skeleton are painted with it, so the mapping lives here and is shared.
const RECTANGLE_FLAG_TO_CSS = {
  [rectangleFlag.yellow]: '#F5C518',
  [rectangleFlag.red]: '#EF4444',
  [rectangleFlag.green]: '#22C55E',
};

// Green is no longer painted around the camera feed or onto the skeleton, but
// the backend can still send it as `rectangleColor` (numeric flag or a raw CSS
// string). Callers run the resolved color through this and treat a match as
// "no color sent" — the flag list covers the values this app actually emits.
const GREEN_CSS_VALUES = new Set(['green', 'lime', 'limegreen', '#0f0', '#00ff00', '#22c55e']);

export const isGreenRectangleColor = value =>
  typeof value === 'string' && GREEN_CSS_VALUES.has(value.trim().toLowerCase());

// Returns a CSS color, or null when the backend has no color to apply
// (flag `none`, missing, or an unknown value) so callers can fall back.
export const resolveRectangleColor = value => {
  if (typeof value === 'string' && value.trim() !== '') {
    return value.trim();
  }

  return RECTANGLE_FLAG_TO_CSS[value] ?? null;
};

export const getWindowDimensions = () => {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
};

export const noImplementedWidthToaster = () => {
  showInfo('not Implemented yet!');
};

export const getValueFromOption = option => {
  const value = Number(option.value);

  return value;
};

export const getValueAsStringFromOption = option => {
  const value = option.value;

  return value;
};

export const getKeyFromOption = option => {
  const label = option.label;

  return label;
};

export const getValueFromOperation = data => {
  const value = Number(data.split('=')[1]);
  return value;
};

const drawBodyPoints = (context, landmarks, height, width, redlandMarks) => {
  for (let i = 0; i < redlandMarks.length; i++) {
    // if green point
    // this function for draw point on canvas and has x and y dimensions and color and radius
    drawPoint(
      context,
      landmarks[i]?.x,
      landmarks[i]?.y,
      redlandMarks[i], // Use a green color for all points,
      15,
      width,
      height
    );
  }
};

const drawPoint = (context, x, y, color, pointSize = 15, width, height) => {
  context.beginPath();
  context.arc(x * width, y * height, pointSize / 2, 0, 2 * Math.PI, false);
  context.fillStyle = color;
  context.fill();
  context.stroke();
};

const DEFAULT_POINT_COLOR = '#8DA12B';
const DEFAULT_CONNECTION_COLOR = 'rgba(141,161,43,0.9)';

const resolveSkeletonPointColors = (landmarks, redlandMarks) => {
  if (typeof redlandMarks === 'string' && redlandMarks.trim() !== '') {
    const values = redlandMarks.split(',').map(value => value?.trim()?.toLowerCase());
    if (values.length >= landmarks.length) {
      return values.map(value => (value === 'r' ? 'red' : DEFAULT_POINT_COLOR));
    }
  }

  return Array.from({ length: landmarks.length }, () => DEFAULT_POINT_COLOR);
};

export async function Draw_skelaton({
  canvasElement,
  height,
  landmark,
  videoElement,
  width,
  redlandMarks,
  pointsConnections,
}) {
  if (videoElement && canvasElement) {
    const video = videoElement;
    const pointColors = resolveSkeletonPointColors(landmark, redlandMarks);

    // prepare canvas for draw skelton and points on it
    const canvctx =
      typeof canvasElement.getContext === 'function' ? canvasElement.getContext('2d') : null;
    // connections array for draw line between joint and next joint
    const connections = [{ connection: [], color: '' }];

    if (video && canvctx) {
      pointsConnections.forEach(element => {
        if (pointColors[element.start] === pointColors[element.end]) {
          connections.push({
            color: pointColors[element.start],
            connection: [element.start, element.end],
          });
        } else {
          connections.push({
            color: DEFAULT_CONNECTION_COLOR,
            connection: [element.start, element.end],
          });
        }
      });
      connections.slice(1).forEach(element => {
        // this code for get (x,y) for every points on canvas and use dimensions for canvas for set this points
        const x1 = landmark[element.connection[0]]['x'] * width;
        const y1 = landmark[element.connection[0]]['y'] * height;
        const x2 = landmark[element.connection[1]]['x'] * width;
        const y2 = landmark[element.connection[1]]['y'] * height;
        canvctx.beginPath();
        canvctx.lineWidth = 5;
        canvctx.strokeStyle = element.color;
        // move to line has this points on canvas and draw line from point 1 to point 2
        canvctx.moveTo(x1, y1);
        canvctx.lineTo(x2, y2);
        canvctx.stroke();
      });
      // draw points on every joints on body of person
      drawBodyPoints(canvctx, landmark, height, width, pointColors);
    }
  }
}

export async function Fliiping_Draw_skelaton({
  canvasElement,
  height,
  landmark,
  videoElement,
  width,
  redlandMarks,
}) {
  if (videoElement && canvasElement) {
    const video = videoElement;
    const pointColors = resolveSkeletonPointColors(landmark, redlandMarks);

    const canvctx =
      typeof canvasElement.getContext === 'function' ? canvasElement.getContext('2d') : null;

    const connections = [{ connection: [], color: '' }];

    // Define a flip map for left-right indices (adjust as per MediaPipe Pose indices)
    const flipMap = {
      0: 0, // Nose
      1: 4, // Left Eye Inner -> Right Eye Inner
      2: 5, // Left Eye -> Right Eye
      3: 6, // Left Eye Outer -> Right Eye Outer
      4: 1, // Right Eye Inner -> Left Eye Inner
      5: 2, // Right Eye -> Left Eye
      6: 3, // Right Eye Outer -> Left Eye Outer
      7: 8, // Left Ear -> Right Ear
      8: 7, // Right Ear -> Left Ear
      9: 10, // Mouth Left -> Mouth Right
      10: 9, // Mouth Right -> Mouth Left
      11: 12, // Left Shoulder -> Right Shoulder
      12: 11, // Right Shoulder -> Left Shoulder
      13: 14, // Left Elbow -> Right Elbow
      14: 13, // Right Elbow -> Left Elbow
      15: 16, // Left Wrist -> Right Wrist
      16: 15, // Right Wrist -> Left Wrist
      17: 18, // Left Pinky -> Right Pinky
      18: 17, // Right Pinky -> Left Pinky
      19: 20, // Left Index -> Right Index
      20: 19, // Right Index -> Left Index
      21: 22, // Left Thumb -> Right Thumb
      22: 21, // Right Thumb -> Left Thumb
      23: 24, // Left Hip -> Right Hip
      24: 23, // Right Hip -> Left Hip
      25: 26, // Left Knee -> Right Knee
      26: 25, // Right Knee -> Left Knee
      27: 28, // Left Ankle -> Right Ankle
      28: 27, // Right Ankle -> Left Ankle
      29: 30, // Left Heel -> Right Heel
      30: 29, // Right Heel -> Left Heel
      31: 32, // Left Foot Index -> Right Foot Index
      32: 31, // Right Foot Index -> Left Foot Index
    };

    if (video && canvctx) {
      // Flip landmarks horizontally
      const flippedLandmark = landmark.map(point => ({
        ...point,
        // x: 1 - point.x, // Flip x-coordinates
        // x: 1 - point.x, // Flip x-coordinates
      }));

      // Flip connections based on the flipMap
      const flippedConnections = pointsConnections.map(([a, b]) => [
        flipMap[a] ?? a,
        flipMap[b] ?? b,
      ]);

      flippedConnections.forEach(Element => {
        if (pointColors[Element[0]] === pointColors[Element[1]]) {
          connections.push({ color: pointColors[Element[0]], connection: Element });
        } else {
          connections.push({
            connection: Element,
            color: DEFAULT_CONNECTION_COLOR,
          });
        }
      });

      connections.slice(1).forEach(element => {
        const x1 = flippedLandmark[element.connection[0]]['x'] * (width / 2);
        const y1 = flippedLandmark[element.connection[0]]['y'] * height;
        const x2 = flippedLandmark[element.connection[1]]['x'] * (width / 2);
        const y2 = flippedLandmark[element.connection[1]]['y'] * height;

        canvctx.beginPath();
        canvctx.lineWidth = 5;
        canvctx.strokeStyle = element.color;
        canvctx.moveTo(x1, y1);
        canvctx.lineTo(x2, y2);
        canvctx.stroke();
      });

      drawBodyPoints(canvctx, flippedLandmark, height, width, pointColors);
    }
  }
}

// Computes the source rectangle (sx, sy, sWidth, sHeight) that CSS
// `object-fit: cover` would show when a `sourceWidth`x`sourceHeight` media
// element is displayed inside a `destWidth`x`destHeight` box — i.e. the
// portion of the source that stays visible after the excess is cropped off
// one axis, centered.
export const getCoverSourceRect = (sourceWidth, sourceHeight, destWidth, destHeight) => {
  if (!sourceWidth || !sourceHeight || !destWidth || !destHeight) {
    return { sx: 0, sy: 0, sWidth: sourceWidth || 0, sHeight: sourceHeight || 0 };
  }

  const sourceRatio = sourceWidth / sourceHeight;
  const destRatio = destWidth / destHeight;

  if (sourceRatio > destRatio) {
    const sHeight = sourceHeight;
    const sWidth = sourceHeight * destRatio;
    return { sx: (sourceWidth - sWidth) / 2, sy: 0, sWidth, sHeight };
  }

  const sWidth = sourceWidth;
  const sHeight = sourceWidth / destRatio;
  return { sx: 0, sy: (sourceHeight - sHeight) / 2, sWidth, sHeight };
};

// Draws `source` (a video or canvas element) onto `ctx` the same way CSS
// `object-fit: cover` would render it inside a `destWidth`x`destHeight` box —
// cropping the excess instead of stretching it, so the pixels drawn match
// what's actually visible on screen.
export const drawImageCover = (ctx, source, sourceWidth, sourceHeight, destWidth, destHeight) => {
  const { sx, sy, sWidth, sHeight } = getCoverSourceRect(
    sourceWidth,
    sourceHeight,
    destWidth,
    destHeight
  );
  ctx.drawImage(source, sx, sy, sWidth, sHeight, 0, 0, destWidth, destHeight);
};

// Computes the destination rectangle CSS `object-fit: contain` would place a
// `sourceWidth`x`sourceHeight` media element into, inside a
// `destWidth`x`destHeight` box — the whole frame scaled down to fit, centered,
// with letterbox/pillarbox bars on the axis that has room left over.
export const getContainDestRect = (sourceWidth, sourceHeight, destWidth, destHeight) => {
  if (!sourceWidth || !sourceHeight || !destWidth || !destHeight) {
    return { dx: 0, dy: 0, dWidth: destWidth || 0, dHeight: destHeight || 0 };
  }

  const scale = Math.min(destWidth / sourceWidth, destHeight / sourceHeight);
  const dWidth = sourceWidth * scale;
  const dHeight = sourceHeight * scale;

  return {
    dx: (destWidth - dWidth) / 2,
    dy: (destHeight - dHeight) / 2,
    dWidth,
    dHeight,
  };
};

// `object-fit: contain` counterpart of drawImageCover — nothing is cropped, so
// every part of the source frame reaches MediaPipe. Used for an uploaded video,
// where cutting off the sides/top of the clip would hide body parts the user
// explicitly chose to analyze.
export const drawImageContain = (ctx, source, sourceWidth, sourceHeight, destWidth, destHeight) => {
  const { dx, dy, dWidth, dHeight } = getContainDestRect(
    sourceWidth,
    sourceHeight,
    destWidth,
    destHeight
  );
  ctx.drawImage(source, 0, 0, sourceWidth, sourceHeight, dx, dy, dWidth, dHeight);
};

// Maps a MediaPipe-normalized point (0-1 over the full source frame) to
// pixel coordinates inside a `destWidth`x`destHeight` box that shows that
// source cropped via `object-fit: cover` — the inverse of `getCoverSourceRect`.
// Points that fall in the cropped-off area land outside the dest box, which
// is correct: that part of the body isn't visible in the cover-cropped view.
export const mapNormalizedPointCover = (x, y, sourceWidth, sourceHeight, destWidth, destHeight) => {
  const { sx, sy, sWidth, sHeight } = getCoverSourceRect(
    sourceWidth,
    sourceHeight,
    destWidth,
    destHeight
  );

  return {
    x: ((x * sourceWidth - sx) / sWidth) * destWidth,
    y: ((y * sourceHeight - sy) / sHeight) * destHeight,
  };
};

// `object-fit: contain` counterpart of mapNormalizedPointCover — maps a
// MediaPipe-normalized point (0-1 over the full source frame) into a
// `destWidth`x`destHeight` box that shows the whole frame letterboxed. Nothing
// is cropped, so every point lands inside the box (inside the drawn image,
// never on the bars).
export const mapNormalizedPointContain = (
  x,
  y,
  sourceWidth,
  sourceHeight,
  destWidth,
  destHeight
) => {
  const { dx, dy, dWidth, dHeight } = getContainDestRect(
    sourceWidth,
    sourceHeight,
    destWidth,
    destHeight
  );

  return { x: dx + x * dWidth, y: dy + y * dHeight };
};

// Draws just the pose skeleton (connections + joints) onto a transparent
// overlay canvas sitting on top of the webcam video. `landmarks` are
// MediaPipe-normalized (0-1) over the *full, uncropped* source frame — the
// detector is handed the raw video element, not a container-shaped canvas — so
// they can't be scaled straight onto the canvas box. They're projected through
// the same object-fit the video is displayed with (`contain` for an uploaded
// clip; for the live webcam whichever the camera-view setting picked — `cover`,
// `contain` or `fill`), which is what makes the joints sit on the body instead of
// drifting. `sourceWidth`/`sourceHeight` are the video's intrinsic dimensions
// (videoWidth/videoHeight).
//
// The canvas carries the same scaleX(-1) mirror transform as the video, so no
// coordinate flipping is needed here. Pass falsy/empty landmarks to just clear
// the overlay (e.g. when the person has left the frame).
export const drawPoseSkeleton = ({
  canvasElement,
  landmarks,
  color = '#FFFFFF',
  sourceWidth,
  sourceHeight,
  objectFit = 'cover',
}) => {
  if (!canvasElement) {
    return;
  }
  const ctx = canvasElement.getContext('2d');
  if (!ctx) {
    return;
  }

  // Keep the backing store in sync with the on-screen size so normalized points
  // land in the right place and the lines stay crisp instead of stretched.
  const displayWidth = canvasElement.clientWidth;
  const displayHeight = canvasElement.clientHeight;
  if (displayWidth && canvasElement.width !== displayWidth) {
    canvasElement.width = displayWidth;
  }
  if (displayHeight && canvasElement.height !== displayHeight) {
    canvasElement.height = displayHeight;
  }

  const { width, height } = canvasElement;
  ctx.clearRect(0, 0, width, height);

  if (!Array.isArray(landmarks) || landmarks.length === 0 || !width || !height) {
    return;
  }

  const hasSourceSize = Boolean(sourceWidth && sourceHeight);
  // Under `contain` the frame does not fill the canvas box: it is scaled down and
  // centered, with bars on one axis. Everything below sizes itself off this
  // rectangle rather than off the box, because that is where the body actually is.
  // `cover` and `fill` both span the whole box, so the rect and the box coincide.
  const drawnRect =
    objectFit === 'contain' && hasSourceSize
      ? getContainDestRect(sourceWidth, sourceHeight, width, height)
      : { dx: 0, dy: 0, dWidth: width, dHeight: height };

  // `fill` stretches the source onto the box, so normalized points already land
  // where they belong and there is no fit math to do. Same treatment when the
  // source dimensions are unknown: fall back to the plain stretch rather than
  // dropping the skeleton entirely.
  const mapPoint =
    objectFit === 'fill' || !hasSourceSize
      ? point => ({ x: point.x * width, y: point.y * height })
      : point =>
          objectFit === 'contain'
            ? mapNormalizedPointContain(point.x, point.y, sourceWidth, sourceHeight, width, height)
            : mapNormalizedPointCover(point.x, point.y, sourceWidth, sourceHeight, width, height);

  // Project once per landmark instead of re-running the fit math on every
  // connector endpoint (each joint is touched by several connections).
  const projected = landmarks.map(point => (point ? mapPoint(point) : null));

  // Line and joint sizes are proportional to the drawn frame, not to the canvas.
  // Scaling them off the box instead left the letterboxed view with strokes and
  // dots sized for a full-width body — visibly too heavy for the smaller one.
  const scaleWidth = drawnRect.dWidth || width;

  // Under `contain` the bars are not part of the picture. MediaPipe can report a
  // landmark slightly outside 0-1 when it extrapolates a limb past the frame
  // edge, and the joint glow has a radius of its own, so without this the overlay
  // paints dots and bloom onto the letterbox. Nothing to clip in the other two
  // modes: the frame is the whole box there, and points cropped away by `cover`
  // are meant to fall outside it.
  const clipsToDrawnRect = objectFit === 'contain' && hasSourceSize;
  if (clipsToDrawnRect) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(drawnRect.dx, drawnRect.dy, drawnRect.dWidth, drawnRect.dHeight);
    ctx.clip();
  }

  // Dashed connector lines between joints (matches the reference look).
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(2, Math.round(scaleWidth / 240));
  const dash = Math.max(6, Math.round(scaleWidth / 55));
  ctx.setLineDash([dash, Math.round(dash * 0.8)]);

  pointsConnections.forEach(([start, end]) => {
    // Nothing is drawn over the face — drop any connector that touches a
    // facial landmark (eyes/ears/nose/mouth) rather than filtering the
    // shared connection list, which other renderers still need whole.
    if (isFaceLandmarkIndex(start) || isFaceLandmarkIndex(end)) {
      return;
    }
    const from = projected[start];
    const to = projected[end];
    if (!from || !to) {
      return;
    }
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.lineTo(to.x, to.y);
    ctx.stroke();
  });
  ctx.setLineDash([]);

  // Glowing joint dots: a colored bloom (via shadowBlur) with a bright warm core.
  const coreRadius = Math.max(3, Math.round(scaleWidth / 130));
  const glowRadius = Math.max(10, Math.round(scaleWidth / 45));

  projected.forEach((point, index) => {
    if (!point || isFaceLandmarkIndex(index)) {
      return;
    }
    const cx = point.x;
    const cy = point.y;

    ctx.save();
    ctx.shadowColor = color;
    ctx.shadowBlur = glowRadius;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(cx, cy, coreRadius, 0, 2 * Math.PI);
    ctx.fill();
    // second pass deepens the bloom
    ctx.fill();
    ctx.restore();

    // bright warm center on top of the glow
    ctx.beginPath();
    ctx.fillStyle = 'rgba(255, 241, 204, 0.95)';
    ctx.arc(cx, cy, coreRadius * 0.55, 0, 2 * Math.PI);
    ctx.fill();
  });

  if (clipsToDrawnRect) {
    ctx.restore();
  }
};

export const scrollToEnd = () => {
  window.scrollTo({
    top: document.documentElement.scrollHeight,
    behavior: 'smooth',
  });
};

export const convertStoMs = seconds => {
  let minutes = ~~(seconds / 60);
  let extraSeconds = seconds % 60;
  return `${minutes < 10 ? `0${minutes}` : minutes}:${
    extraSeconds < 10 ? `0${extraSeconds}` : extraSeconds
  }`;
};

export const roundToBiggest = number => {
  const result = Math.ceil(number / 10) * 10;
  return result;
};

// #endregion

export const getScreenShotFromVideo = ({ videoElement, timeInSeconds }) => {
  return new Promise((resolve, reject) => {
    // Check if videoElement is passed directly and exists
    if (!videoElement) {
      return reject(new Error('Video element is not available.'));
    }

    const takeScreenshot = () => {
      const canvas = document.createElement('canvas');
      canvas.width = videoElement.videoWidth * 0.25;
      canvas.height = videoElement.videoHeight * 0.25;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

      // const ImageUrl = canvas.toDataURL("image/png");
      canvas.toBlob(blob => {
        if (blob) {
          const imageUrl = URL.createObjectURL(blob);
          resolve(imageUrl); // Resolve the promise with the image URL
        } else {
          reject(new Error('Failed to create Blob from canvas.'));
        }
      }, 'image/png');
      // Clean up the event listener after the screenshot is taken
      videoElement.removeEventListener('seeked', takeScreenshot);
      // return ImageUrl;
    };

    const onSeeked = () => {
      takeScreenshot();
    };

    // Error handling for the seek operation
    const onError = err => {
      console.error('Video error:', err);
      videoElement.removeEventListener('seeked', onSeeked); // Clean up on error
      reject(new Error('Error seeking video.'));
    };

    // Add event listener to take screenshot after seeking is complete
    videoElement.addEventListener('seeked', onSeeked);

    // Add error event listener
    videoElement.addEventListener('error', onError);

    // Set the video time (this will trigger the seeked event)
    try {
      videoElement.currentTime = timeInSeconds;
    } catch (error) {
      videoElement.removeEventListener('seeked', onSeeked); // Clean up on failure
      videoElement.removeEventListener('error', onError);
      reject(new Error('Failed to set video currentTime.'));
    }
  });
};

export const drawPointOnCanvas = ({ x, y, ctx, color, pointRadius = 5 }) => {
  // Draw the point (as a circle)
  ctx.beginPath();
  ctx.arc(x, y, pointRadius, 0, Math.PI * 2, true);
  ctx.fillStyle = color; // Set the point color
  ctx.fill();
  ctx.closePath();
};

// this code use for format size for specific form like (10Kb or 10Mb) from bytes
export const formatFileSize = size => {
  if (size < 1024) {
    return `${size} bytes`;
  } else if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(2)} KB`;
  } else {
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  }
};

export const formatTime = timeInSeconds => {
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = timeInSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.00`;
};

// Camera/microphone permission + device enumeration moved to
// services/camera/ (CameraManager) — see requestPermission()/enumerateCameras()
// there. This used to be two separate getUserMedia probes
// (getMediaDevices/requestMediaPermissions) called back-to-back on every
// /camera-permission mount; CameraManager keeps and reuses the one real
// stream instead.

export const removeFromLocalStorageInPerformance = () => {
  localStorage.removeItem('redlandmark');
  localStorage.removeItem('inPerformance');
  localStorage.removeItem('isPlaySound');
};

// this code for remove from local storage when user navigate to post and category pages
export const removeFromLocalStorage = () => {
  localStorage.removeItem('initialConfig');
  localStorage.removeItem('video-url');
  localStorage.removeItem('redlandmark');
  localStorage.removeItem('isFlip');
};

export const timers = {};

export const convertToSeconds = time => {
  return (time / 1000).toFixed(0);
};

export const hasTimer = key => Boolean(timers[key]);

/**
 * Remaining exercise time as "MM:SS", counting down from the set's own budget
 * (`set_eta_seconds`). Shared so the desktop overlay timer and the mobile stat
 * bar can't drift apart.
 *
 * Two guards, both of which used to surface as a countdown that looked broken —
 * and most visibly offline, where the countdown is the only live readout on the
 * screen:
 *
 *  - Before `userTimer` is anchored (the socket has to reach IN_PERFORMANCE and
 *    the get-ready countdown has to finish first), getTimer returns 0, which
 *    rendered as "00:00" — a countdown that reads as already finished. Show the
 *    full budget instead; it starts ticking the moment the timer is anchored.
 *  - A missing or non-positive budget makes getTimer throw on the Decrease
 *    strategy, which takes the whole page down with it. There is no countdown to
 *    render in that case, so return "00:00" rather than crashing.
 */
export const resolveRemainingExerciseTime = ({ time, totalTimeSeconds }) => {
  const totalSeconds = Number(totalTimeSeconds);

  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
    return convertStoMs(0);
  }

  if (!hasTimer('userTimer')) {
    return convertStoMs(Math.round(totalSeconds));
  }

  return convertStoMs(
    convertToSeconds(
      getTimer({
        key: 'userTimer',
        timer: time,
        totalValue: totalSeconds * 1000,
        strategy: 'Decrease',
      })
    )
  );
};

export const addTimer = ({ key, value }) => {
  timers[key] = {
    startTime: value,
    isPaused: false,
    pauseStartTime: 0,
    totalPauseTime: 0,
    lastPauseTime: 0,
  };
};

export const getTimer = ({ key, timer, strategy = 'Increase', totalValue = 0 }) => {
  if (strategy === 'Decrease' && !totalValue) {
    throw new Error('Total value is required for decrease strategy');
  }

  // Timer not initialized yet (addTimer hasn't run for this key) — return a
  // clean 0 instead of falling through to NaN arithmetic below.
  if (!timers[key]) {
    return 0;
  }

  if (timers[key]?.isPaused) {
    const currentTime =
      timers[key]?.pauseStartTime - timers[key]?.startTime - timers[key]?.totalPauseTime;

    return strategy === 'Decrease' ? totalValue - currentTime : currentTime;
  } else {
    if (strategy === 'Increase') {
      return Math.floor(timer - timers[key]?.startTime - timers[key]?.totalPauseTime);
    } else if (strategy === 'Decrease') {
      const currentTime =
        totalValue - (timer - timers[key]?.startTime - timers[key]?.totalPauseTime);
      return currentTime > 0 ? Math.ceil(currentTime) : 0;
    }
  }
};

export const pauseTimer = ({ key, time, force = false }) => {
  if (timers[key]) {
    if (timers[key]?.isPaused && !force) {
      return;
    } else {
      timers[key].isPaused = true;
      timers[key].pauseStartTime = time;
    }
  }
};

export const playTimer = ({ key }) => {
  if (timers[key].isPaused) {
    timers[key].isPaused = false;
  }
};

export const resumeTimer = ({ key, time }) => {
  if (timers[key]?.isPaused) {
    const currentPauseTime = time - timers[key]?.pauseStartTime;
    timers[key].totalPauseTime = totalPauseTime({ key, currentPauseTime });
    timers[key].isPaused = false;
    timers[key].pauseStartTime = 0;
    timers[key].lastPauseTime = currentPauseTime;
  } else {
    return;
  }
};

export const isPaused = ({ key }) => {
  return timers[key]?.isPaused;
};

export const getPauseTime = ({ key }) => {
  return timers[key]?.pauseStartTime;
};

export const totalPauseTime = ({ key, currentPauseTime }) => {
  return timers[key].totalPauseTime + currentPauseTime;
};

export const resetTimer = ({ key, time }) => {
  timers[key].startTime = time;
  timers[key].pauseStartTime = 0;
  timers[key].totalPauseTime = 0;
  timers[key].lastPauseTime = 0;
  if (timers[key].isPaused) {
    // if the timer is paused, then we need to set the pauseStartTime to the current time
    pauseTimer({ key, time, force: true });
  }
};

export const getLastPauseTime = ({ key }) => {
  return timers[key]?.lastPauseTime;
};

export const getTotalPauseTime = ({ key }) => {
  return timers[key]?.totalPauseTime;
};

export const deleteTimer = ({ key }) => {
  delete timers[key];
};

export const handleSchedule = ({ delay, callbackFunction, timerIsRunning }) => {
  if (timerIsRunning) {
    let timeout = setTimeout(() => {
      callbackFunction();
      clearTimeout(timeout);
    }, delay);
  } else {
    console.log('Timer is not running');
  }
};

export const calculatePacing = ({ reps, pacing, holdingTime, isHolding }) => {
  let value;
  const mustToCalculate = isHolding
    ? Number(reps) * (1.5 * Number(holdingTime)) * 4
    : Number(reps) * 1.5 * (60 / Number(pacing));
  value = roundToBiggest(mustToCalculate);
  return { value };
};
